import React, { useEffect, useState } from 'react';
import { DateInput } from '@mantine/dates';
import '@mantine/dates/styles.css';
import { Button, Group } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { CreateAppSubscriptionData } from '@/Api';
import { IAppLicense } from '@/types/App.model';

const EMPTY_FORM: CreateAppSubscriptionData = {
  email: '',
  appLicense_id: '',
  expirationDate: '',
};

interface UserLicenseFormProps {
  title: string;
  submitButtonText: string;
  /** Seed values when the form mounts / resets (keep referentially stable or pass a resetKey). */
  initialValues?: CreateAppSubscriptionData;
  /** Bump to re-seed local state when the modal re-opens with the same initial object. */
  resetKey?: string | number | boolean;
  appLicenses: IAppLicense[];
  onSubmit: (data: CreateAppSubscriptionData) => void;
  onCancel: () => void;
  variant?: 'add' | 'edit';
  primaryButtonColor?: string;
  primaryButtonStyle?: React.CSSProperties;
  secondaryButtonStyle?: React.CSSProperties;
}

const formStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  borderRadius: '6px',
  border: '1px solid var(--mantine-color-gray-3)',
  fontSize: '14px',
  backgroundColor: 'var(--mantine-color-body)',
  color: 'var(--mantine-color-text)',
};

/**
 * Owns its own form state so keystrokes do not re-render the heavy admin page.
 */
export const UserLicenseForm: React.FC<UserLicenseFormProps> = ({
  title,
  submitButtonText,
  initialValues = EMPTY_FORM,
  resetKey,
  appLicenses,
  onSubmit,
  onCancel,
  variant = 'add',
  primaryButtonColor,
  primaryButtonStyle,
  secondaryButtonStyle,
}) => {
  const [formData, setFormData] = useState<CreateAppSubscriptionData>(initialValues);

  useEffect(() => {
    setFormData(initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-seed only when modal open cycle changes
  }, [resetKey]);

  const containerStyle: React.CSSProperties = {
    ...(variant === 'add' && title
      ? {
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          padding: '16px',
          marginBottom: '16px',
        }
      : {
          padding: '0',
        }),
  };

  return (
    <div style={containerStyle}>
      {title && (
        <h3
          style={{
            margin: '0 0 16px 0',
            fontSize: '16px',
            fontWeight: '500',
          }}
        >
          {title}
        </h3>
      )}
      <div style={formStyle}>
        <div>
          <label
            style={{
              display: 'block',
              marginBottom: '4px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            Email Address
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
            style={inputStyle}
            placeholder="user@example.com"
          />
        </div>

        <div>
          <label
            style={{
              display: 'block',
              marginBottom: '4px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            License
          </label>
          <select
            value={formData.appLicense_id}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, appLicense_id: e.target.value }))
            }
            style={inputStyle}
          >
            <option value="" disabled>
              Select a license
            </option>
            {appLicenses.map((license) => (
              <option key={license._id} value={license._id}>
                {license.licenseName}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            style={{
              display: 'block',
              marginBottom: '4px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            Expiry Date
          </label>
          <div style={{ position: 'relative' }}>
            <DateInput
              value={formData.expirationDate ? new Date(formData.expirationDate) : null}
              onChange={(date: Date | null) =>
                setFormData((prev) => ({
                  ...prev,
                  expirationDate: date ? date.toISOString() : '',
                }))
              }
              placeholder="Select date"
              valueFormat="DD.MM.YYYY"
              style={{ ...inputStyle, width: '100%' }}
            />
          </div>
        </div>

        <Group justify="flex-end" mt="md">
          <Button
            variant="subtle"
            color="gray"
            onClick={onCancel}
            style={{
              fontWeight: 500,
              padding: '8px 16px',
              ...secondaryButtonStyle,
            }}
            size="md"
          >
            Cancel
          </Button>
          <Button
            onClick={() => onSubmit(formData)}
            leftSection={<IconCheck size={18} />}
            style={{
              backgroundColor: primaryButtonColor,
              fontWeight: 500,
              padding: '8px 16px',
              ...primaryButtonStyle,
            }}
            size="md"
          >
            {submitButtonText}
          </Button>
        </Group>
      </div>
    </div>
  );
};
